import { Injectable } from '@angular/core';
import { BzgToastType } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { ShowToastService } from 'app/shared/services/showToast.service';

export interface ToastConfig {
  type: BzgToastType;
  duration?: number;
  verticalPosition?: 'top' | 'bottom';
  horizontalPosition?: 'left' | 'center' | 'right';
  showCountDown?: boolean;
  fullwidth?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  private readonly defaultConfig: Partial<ToastConfig> = {
    verticalPosition: 'bottom',
    horizontalPosition: 'right',
    showCountDown: false,
    fullwidth: false
  };

  constructor(
    private _translateService: TranslateService,
    private _showToastService: ShowToastService
  ) { }

  showSuccess(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'success');
  }

  showError(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'error');
  }

  showInfo(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'info');
  }

  showWarning(message: string, config?: Partial<ToastConfig>): void {
    this._showToastService.showToast(message, 'warning');
  }

  showLocalizedToast(
    messageKey: string, 
    messageParams?: string[], 
    config?: Partial<ToastConfig>
  ): void {
    const message = this.translateMessageWithParams(messageKey, messageParams);
    const finalConfig = { ...this.defaultConfig, ...config };
    this._showToastService.showToast(message, 'success');
  }

  private translateMessageWithParams(messageKey: string, messageParams?: string[]): string {
    let message = this._translateService.instant(messageKey);
    
    if (messageParams) {
      messageParams.forEach((param, index) => {
        message = message.replace(`{${index}}`, param);
      });
    }
    
    return message;
  }

  private parseErrorMessage(errorMessage: string): { key: string; param?: string } {
    const errorParts = errorMessage.split('|');
    return {
      key: errorParts[0] || 'BASEKNOWLEDGECATALOG.ERRORS.ERROR_READING_FILE',
      param: errorParts[1]
    };
  }

  showInvalidFilesError(invalidFiles: any[]): void {
    const invalidFilesTitle = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.INVALID_FILES_FOUND');
    
    // Crear mensajes de error individuales
    invalidFiles.forEach((invalidFile) => {
      const { key, param } = this.parseErrorMessage(invalidFile.error);
      let translatedError = this.translateMessageWithParams(key, param ? [param] : undefined);
      
      // Si la traducción falla o está vacía, mostrar el error original
      if (!translatedError || translatedError === key) {
        translatedError = invalidFile.error || 'Unknown error';
      }
      
      const fileName = invalidFile.file?.name || 'Unknown file';
      
      // Acortar el nombre del archivo para que el mensaje no sea muy largo
      let displayName = fileName;
      if (fileName.length > 40) {
        const extension = fileName.substring(fileName.lastIndexOf('.'));
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        displayName = nameWithoutExt.substring(0, 35) + '...' + extension;
      }
      
      // Mensaje en formato: "Título - Archivo: Error"
      const message = `${invalidFilesTitle} - ${displayName}: ${translatedError}`;
      
      this._showToastService.showToast(message, 'warning');
    });
  }

  showFileConversionError(error: any): void {
    const errorMsg = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.ERROR_CONVERTING_FILES');
    const fullMessage = `${errorMsg}: ${error.message}`;
    
    this._showToastService.showToast(fullMessage, 'warning');
  }

  showMaxFilesExceededError(errorMessage: string): void {
    const { key, param } = this.parseErrorMessage(errorMessage);
    const translatedError = this.translateMessageWithParams(key, param ? [param] : undefined);
    
    this._showToastService.showToast(translatedError, 'warning');
  }

  showPasswordProtectedFilesWarning(files: File[]): void {
    const uploadFilesLabel = this._translateService.instant('BASEKNOWLEDGECATALOG.UPLOAD_FILES');
    const validationFailedLabel = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.VALIDATION_FAILED');
    const passwordProtectedMessage = this._translateService.instant('BASEKNOWLEDGECATALOG.ERRORS.PASSWORD_PROTECTED_MESSAGE');
    
    // Mostrar un toast por cada archivo protegido
    files.forEach(file => {
      const extension = file.name.split('.').pop()?.toLowerCase();
      let documentType = 'document';
      
      if (extension === 'pdf') {
        documentType = 'PDF';
      } else if (extension === 'doc' || extension === 'docx') {
        documentType = 'Word';
      } else if (extension === 'xls' || extension === 'xlsx') {
        documentType = 'Excel';
      }
      
      // Acortar el nombre del archivo si es muy largo
      let displayName = file.name;
      if (file.name.length > 40) {
        const ext = file.name.substring(file.name.lastIndexOf('.'));
        const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
        displayName = nameWithoutExt.substring(0, 35) + '...' + ext;
      }
      
      const message = `${uploadFilesLabel} - ${displayName}: ${documentType} ${passwordProtectedMessage}`;
      
      this._showToastService.showToast(message, 'warning');
    });
  }
}
